import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import AIBackgroundEffects from "./AIBackgroundEffects";
import API_CONFIG, { buildApiUrl } from "../config/api.config";
import "./SuccessPage.css";

const SuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { subscriptionId, email, businessName } = location.state || {};
  const [agentStatus, setAgentStatus] = useState("creating");
  const [agentTestUrl, setAgentTestUrl] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    // Redirect if no state data
    if (!subscriptionId) {
      navigate("/");
      return;
    }

    // Check agent status periodically
    const checkAgentStatus = async () => {
      setCheckingStatus(true);
      try {
        const response = await axios.get(
          buildApiUrl(API_CONFIG.ENDPOINTS.GET_AGENT_STATUS(subscriptionId))
        );

        if (response.data.success) {
          setAgentStatus(response.data.data.agentStatus);
          if (response.data.data.agentTestUrl) {
            setAgentTestUrl(response.data.data.agentTestUrl);
          }
        }
      } catch (error) {
        console.error("Error checking agent status:", error);
      } finally {
        setCheckingStatus(false);
      }
    };

    // Initial check
    checkAgentStatus();

    // Set up periodic checking if agent is still being created
    const interval = setInterval(() => {
      if (agentStatus === "creating" || agentStatus === "not_created") {
        checkAgentStatus();
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [subscriptionId, navigate, agentStatus]);

  if (!subscriptionId) {
    return null;
  }

  return (
    <div className="success-container">
      {/* AI Background Effects - Success */}
      <AIBackgroundEffects step={3} />

      <div className="success-card">
        <div className="success-icon">
          <svg
            width="80"
            height="80"
            viewBox="0 0 80 80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="40" cy="40" r="40" fill="#28a745" />
            <path
              d="M55 30L35 50L25 40"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h1 className="success-title"> ¡Tu Agente de Voz está Listo!</h1>

        <div className="success-details">
          <p className="success-message">
            ¡Excelente! Tu agente de voz IA para {businessName} ha sido creado y
            está listo para usar. Ha sido entrenado con la información de tu
            negocio y puede comenzar a manejar consultas de clientes
            inmediatamente.
          </p>

          <div className="subscription-info">
            <div className="info-item">
              <span className="info-label">Nombre del Negocio:</span>
              <span className="info-value">{businessName}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Correo Electrónico:</span>
              <span className="info-value">{email}</span>
            </div>
            <div className="info-item">
              <span className="info-label">ID de Suscripción:</span>
              <span className="info-value subscription-id">
                {subscriptionId}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Plan Mensual:</span>
              <span className="info-value">$39.00/mes</span>
            </div>
          </div>

          {/* Agent Test URL Section */}
          <div className="agent-test-section">
            <h3 className="agent-test-title">🤖 Prueba Tu Agente de Voz</h3>
            {agentStatus === "creating" && (
              <div className="agent-status-message creating">
                <div className="loading-spinner-inline"></div>
                <p>
                  Finalizando la configuración de tu agente de voz... ¡Casi
                  listo!
                </p>
                {checkingStatus && (
                  <span className="checking-text">Verificando estado...</span>
                )}
              </div>
            )}
            {agentStatus === "ready" && agentTestUrl && (
              <div className="agent-status-message ready">
                <p className="success-indicator">
                  ✅ ¡Tu agente de voz está listo!
                </p>
                <div className="test-url-container">
                  <label className="test-url-label">URL de Prueba:</label>
                  <div className="test-url-box">
                    <input
                      type="text"
                      value={agentTestUrl}
                      readOnly
                      className="test-url-input"
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      className="copy-button"
                      onClick={() => {
                        navigator.clipboard.writeText(agentTestUrl);
                        alert("¡URL copiada al portapapeles!");
                      }}
                      title="Copiar al portapapeles"
                    >
                      📋 Copiar
                    </button>
                  </div>
                  <a
                    href={agentTestUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="test-agent-button"
                  >
                    🎤 Prueba Tu Agente Ahora
                  </a>
                </div>
              </div>
            )}
            {agentStatus === "failed" && (
              <div className="agent-status-message failed">
                <p>
                  ⚠️ Hubo un problema al crear tu agente. Nuestro equipo ha sido
                  notificado.
                </p>
                <p>Por favor contacta a soporte para obtener ayuda.</p>
              </div>
            )}
          </div>
        </div>

        <div className="next-steps">
          <h2>¿Qué Sigue Ahora?</h2>
          <ol className="steps-list">
            <li>
              <strong>¡Tu Agente está Listo!</strong> Tu agente de voz IA ha
              sido creado y entrenado con la información de tu negocio. Puedes
              probarlo inmediatamente usando el enlace de arriba.
            </li>
            <li>
              <strong>Configuración de Pago:</strong> Recibirás un correo
              electrónico con instrucciones de pago para activar tu suscripción
              de $39/mes.
            </li>
            <li>
              <strong>Soporte de Integración:</strong> Nuestro equipo está
              disponible para ayudarte a integrar el agente de voz con tu
              sistema telefónico o sitio web.
            </li>
            <li>
              <strong>Aprendizaje Continuo:</strong> Tu agente continuará
              mejorando a medida que interactúa con tus clientes y aprende de
              los datos de tu negocio.
            </li>
          </ol>
        </div>

        <div className="action-buttons">
          <button
            className="primary-button"
            onClick={() =>
              (window.location.href = "mailto:support@service-intel.com")
            }
          >
            Contactar Soporte
          </button>
          <button className="secondary-button" onClick={() => navigate("/")}>
            Volver al Inicio
          </button>
        </div>

        <div className="support-info">
          <p>
            ¿Necesitas ayuda inmediata? Contacta a nuestro equipo de soporte:
          </p>
          <div className="contact-details">
            <span>📧 support@service-intel.com</span>
            <span>📞 877-328-8625</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
